import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCollection } from "@/hooks/firestore";
import { useAddDocument } from "@/hooks/firestore/operations";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, CheckCircle2 } from "lucide-react";

export const InitializePermissions = () => {
  const [initializing, setInitializing] = useState(false);
  const { documents: permissions, loading: permissionsLoading } = useCollection('permissions');
  
  // Instantiate the add document hook for the 'permissions' collection
  const { addDocument, loading: addingDocument, error: addError } = useAddDocument('permissions');

  // Default permissions that should exist in the system
  const defaultPermissions = [
    { identifier: "user", description: "Basic user permissions - Create and edit brands, drinks, locations, sightings and reviews" },
    { identifier: "editor", description: "Can edit content that others have created" },
    { identifier: "manager_view", description: "Access to management views and dashboards" },
    { identifier: "view_technical", description: "Access to technical implementation details and documentation" },
    { identifier: "manage_flavors", description: "Can create, edit and delete flavors" },
    { identifier: "manage_attributes", description: "Can create, edit and delete attributes" },
    { identifier: "manage_users", description: "Can manage user permissions" },
  ];

  // Check which permissions already exist
  const existingPermissionIds = new Set(permissions?.map(p => p.identifier) || []);
  const missingPermissions = defaultPermissions.filter(p => !existingPermissionIds.has(p.identifier));

  // Return null if all permissions are already initialized or still loading
  // We need permissionsLoading to be false AND missingPermissions.length to be 0
  if (!permissionsLoading && missingPermissions.length === 0) {
    console.log("All permissions already initialized."); // Optional: Add a log
    return null;
  }

  const handleInitialize = async (): Promise<void> => {
    setInitializing(true);
    let addedCount = 0;
    let failedCount = 0;

    try {
      // Add each missing permission using the hook's addDocument
      const promises = missingPermissions.map(async (permission) => {
         // Prepare the data, omitting 'id' and 'createdAt' as the hook handles them
         const permissionData = {
           identifier: permission.identifier,
           description: permission.description
         };
         const newId = await addDocument(permissionData); 
         if (newId) {
           addedCount++;
         } else {
           failedCount++;
           console.error(`Failed to add permission '${permission.identifier}': ${addError?.message}`);
           // We capture the error from the hook, but toast individually might be noisy
         }
      });
      
      await Promise.all(promises);
      
      if (failedCount > 0) {
         toast.error(`Failed to add ${failedCount} permissions. Check console.`);
      } 
      if (addedCount > 0) {
          toast.success(`Added ${addedCount} missing permissions`);
      }
      // Force a re-check or rely on the listener to update the permissions state
      // Maybe add a manual refresh call if the useCollection hook supports it?
      // For now, the UI will update when the collection listener fires.

    } catch (error) {
      // Catch any unexpected errors during Promise.all or setup
      console.error("Error initializing permissions:", error);
      toast.error("An unexpected error occurred during initialization");
    } finally {
      setInitializing(false);
    }
  };

  // Combine loading states
  const isLoading = permissionsLoading || initializing || addingDocument;

  if (isLoading && !permissions) { // Show loading only on initial load
    return <div>Loading permission status...</div>;
  }

  // Only render the card if there are missing permissions or if still loading status
  if (missingPermissions.length === 0 && !permissionsLoading) {
      // This case should ideally be handled by the early return, but as a safeguard:
       return (
            <Card>
              <CardHeader>
                <CardTitle>Permission Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                  <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span>All required permissions are configured.</span>
                  </div>
              </CardContent>
            </Card>
       );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Permission Configuration Required</CardTitle>
        <CardDescription>
          Some standard permission definitions are missing and need to be added to the database.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {defaultPermissions.map(permission => (
              <div 
                key={permission.identifier}
                className="flex items-start gap-2 p-2 border rounded-md"
              >
                {existingPermissionIds.has(permission.identifier) ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="font-medium">{permission.identifier}</p>
                  <p className="text-sm text-muted-foreground">{permission.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <div>
              {missingPermissions.length > 0 ? (
                <p className="text-sm text-amber-500">
                  {missingPermissions.length} permissions need to be added. Click initialize.
                </p>
              ) : (
                 // This state might be briefly visible if loading finishes before the component unmounts
                 <p className="text-sm text-green-500">
                     All required permissions are present.
                 </p>
              )}
            </div>
            <Button 
              onClick={handleInitialize} 
              disabled={isLoading || missingPermissions.length === 0} // Disable button if loading or nothing missing
            >
              {isLoading ? "Processing..." : "Initialize Missing Permissions"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 